/**
 * The small Markdown subset page docs are written in (see src/docs/README.md):
 * headings, paragraphs, lists with one nested level, tables, `>` notes,
 * fenced code and ```flow diagrams. Inline marks are parsed at render time.
 */

export type ListItem = { text: string; children?: ListBlock }
export type ListBlock = { type: 'list'; ordered: boolean; items: ListItem[] }
export type FlowEdge = { from: string; to: string; label?: string }

export type Block =
  | { type: 'heading'; level: 2 | 3; text: string; id: string }
  | { type: 'paragraph'; text: string }
  | ListBlock
  | { type: 'table'; head: string[]; rows: string[][]; align: ('left' | 'right' | 'center')[] }
  | { type: 'note'; blocks: Block[] }
  | { type: 'flow'; edges: FlowEdge[] }
  | { type: 'code'; text: string }

export type DocSections = { short: string; long: string; form?: string }

const MARKER = /^<!--\s*(short|long|form)\s*-->\s*$/

/** Splits a docs file into its «Кратко», «Подробно» and form parts. */
export function splitSections(source: string): DocSections {
  const parts: Record<string, string[]> = {}
  let current: string | null = null
  for (const line of source.replace(/\r\n?/g, '\n').split('\n')) {
    const marker = MARKER.exec(line.trim())
    if (marker) {
      current = marker[1]
      parts[current] = []
    } else if (current) parts[current].push(line)
  }
  const text = (key: string) => parts[key]?.join('\n').trim() ?? ''
  return { short: text('short'), long: text('long'), form: text('form') || undefined }
}

const ARROW = /\s*(?:->|→)\s*/

/** `A -> B -> C : label` — a chain of edges; the label goes on every edge of the line. */
export function parseFlow(text: string): FlowEdge[] {
  const edges: FlowEdge[] = []
  for (const raw of text.split('\n')) {
    const line = raw.trim()
    if (!line || line.startsWith('#')) continue
    const colon = line.lastIndexOf(' : ')
    const body = colon >= 0 ? line.slice(0, colon) : line
    const label = colon >= 0 ? line.slice(colon + 3).trim() : undefined
    const nodes = body.split(ARROW).map((node) => node.trim())
    if (nodes.length < 2 || nodes.some((node) => !node)) throw new Error(`flow: "${line}" is not "A -> B"`)
    for (let index = 1; index < nodes.length; index++) edges.push({ from: nodes[index - 1], to: nodes[index], label })
  }
  return edges
}

const splitRow = (line: string) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split(/(?<!\\)\|/)
    .map((cell) => cell.trim().replace(/\\\|/g, '|'))

const LIST_ITEM = /^(\s*)([-*]|\d+[.)])\s+(.*)$/
const isTableSeparator = (line: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line)

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const blocks: Block[] = []
  let headings = 0
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const trimmed = line.trim()
    if (!trimmed || /^<!--.*-->$/.test(trimmed)) {
      index++
      continue
    }

    const fence = /^```\s*([\w-]*)\s*$/.exec(trimmed)
    if (fence) {
      const body: string[] = []
      index++
      while (index < lines.length && !/^```\s*$/.test(lines[index].trim())) body.push(lines[index++])
      index++
      const text = body.join('\n')
      blocks.push(fence[1] === 'flow' ? { type: 'flow', edges: parseFlow(text) } : { type: 'code', text })
      continue
    }

    const heading = /^(#{2,3})\s+(.*)$/.exec(trimmed)
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length as 2 | 3, text: heading[2].trim(), id: `s${++headings}` })
      index++
      continue
    }

    if (trimmed.startsWith('>')) {
      const body: string[] = []
      while (index < lines.length && lines[index].trim().startsWith('>')) body.push(lines[index++].trim().replace(/^>\s?/, ''))
      blocks.push({ type: 'note', blocks: parseMarkdown(body.join('\n')) })
      continue
    }

    if (trimmed.startsWith('|') && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const head = splitRow(trimmed)
      const align = splitRow(lines[index + 1]).map((cell) =>
        cell.startsWith(':') && cell.endsWith(':') ? 'center' : cell.endsWith(':') ? 'right' : 'left',
      ) as ('left' | 'right' | 'center')[]
      const rows: string[][] = []
      index += 2
      while (index < lines.length && lines[index].trim().startsWith('|')) rows.push(splitRow(lines[index++]))
      blocks.push({ type: 'table', head, rows, align })
      continue
    }

    if (LIST_ITEM.test(line)) {
      const root: ListBlock = { type: 'list', ordered: /\d/.test(LIST_ITEM.exec(line)![2]), items: [] }
      const baseIndent = LIST_ITEM.exec(line)![1].length
      while (index < lines.length) {
        const current = lines[index]
        const item = LIST_ITEM.exec(current)
        if (item) {
          const indent = item[1].length
          if (indent > baseIndent && root.items.length) {
            const parent = root.items[root.items.length - 1]
            parent.children ??= { type: 'list', ordered: /\d/.test(item[2]), items: [] }
            parent.children.items.push({ text: item[3] })
          } else root.items.push({ text: item[3] })
          index++
        } else if (current.trim() && /^\s{2,}/.test(current) && root.items.length) {
          // A wrapped continuation line of the last item.
          const parent = root.items[root.items.length - 1]
          const target = parent.children?.items.at(-1) ?? parent
          target.text += ` ${current.trim()}`
          index++
        } else break
      }
      blocks.push(root)
      continue
    }

    const body: string[] = []
    while (index < lines.length) {
      const current = lines[index].trim()
      if (!current || /^(#{2,3}\s|```|>|\|)/.test(current) || LIST_ITEM.test(lines[index])) break
      body.push(current)
      index++
    }
    blocks.push({ type: 'paragraph', text: body.join(' ') })
  }
  return blocks
}

export type Inline =
  | { type: 'text'; text: string }
  | { type: 'strong' | 'em'; children: Inline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: Inline[] }

const INLINE = /(`[^`]+`)|(\*\*[^*]+?\*\*)|(\*[^*\s][^*]*?\*)|(\[[^\]]+\]\([^)\s]+\))/

export function parseInline(text: string): Inline[] {
  const out: Inline[] = []
  let rest = text
  while (rest) {
    const match = INLINE.exec(rest)
    if (!match) {
      out.push({ type: 'text', text: rest })
      break
    }
    if (match.index) out.push({ type: 'text', text: rest.slice(0, match.index) })
    const token = match[0]
    if (match[1]) out.push({ type: 'code', text: token.slice(1, -1) })
    else if (match[2]) out.push({ type: 'strong', children: parseInline(token.slice(2, -2)) })
    else if (match[3]) out.push({ type: 'em', children: parseInline(token.slice(1, -1)) })
    else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)!
      out.push({ type: 'link', href: link[2], children: parseInline(link[1]) })
    }
    rest = rest.slice(match.index + token.length)
  }
  return out
}

/** Plain text of an inline string (for headings in the table of contents, search). */
export const plainText = (text: string) =>
  text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*`]/g, '')
    .trim()
